import { ConfigProvider } from "antd";
import React, { Suspense, useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import "./App.css";
import AdminRoute from "./components/Admin/AdminRoute";
import AccountDeactivated from "./components/AccountDeactivated";
import VerifyEmailRequired from "./components/VerifyEmailRequired";
import AuthPage from "./components/Auth/AuthPage";
import ElectronAuthCallback from "./components/Auth/ElectronAuthCallback";
import EmailActionHandler from "./components/Auth/EmailActionHandler";
import ForgotPassword from "./components/Auth/ForgotPassword";

import { CookieBanner } from "./components/CookieConsent";
import PricingPage from "./components/Billing/PricingPage";
import PostsPage from './components/Posts/PostsPage';
import { getPosts, markUpdatesAsSeen } from './services/postApi';
import SinglePostPage from './components/Posts/SinglePost/SinglePostPage';
import ProtectedRoute from "./components/ProtectedRoute";
import { InvitationLandingPage } from "./components/Referrals";
import { SkillDetailPage, SkillsPage } from "./components/Skills";
import { SupportDocsPage } from "./components/Support";
import { LinearLandingPage } from "./components/Linear";
import { OpenClawLandingPage } from "./components/OpenClaw";
import AIEmployeesLandingPage from "./components/AIEmployees/AIEmployeesLandingPage";
import RoleLandingPage from "./components/AIEmployees/roles/RoleLandingPage";
import ComparisonPage from "./components/AIEmployees/compare/ComparisonPage";
import CompareHubPage from "./components/AIEmployees/compare/CompareHubPage";
import BenchmarksPage from "./components/AIEmployees/compare/BenchmarksPage";
import AIOrganizationPage from "./components/AIEmployees/compare/AIOrganizationPage";
import ProductHuntPage from "./components/AIEmployees/compare/ProductHuntPage";

import VerticalLandingPage from "./components/AIEmployees/verticals/VerticalLandingPage";
import MeetingsLandingPage from "./components/AIEmployees/meetings/MeetingsLandingPage";
import MarketingRoute from "./components/Marketing/MarketingRoute";
import { isAdaptiveAuthPath } from "./components/Marketing/adaptivePaths";
// Tutorials (CEL-1157): STATIC imports by design — prerendered marketing
// surface; lazying them would swap already-painted static HTML to a blank
// fallback on boot (the inverse of the AppCore auth-split rule).
import TutorialsHubPage from "./components/Tutorials/TutorialsHubPage";
import TutorialVideoPage from "./components/Tutorials/TutorialVideoPage";
// Podcast (CEL-1355): STATIC imports by design — same prerendered-marketing
// rule as tutorials (lazying swaps painted static HTML for a blank fallback).
import PodcastHubPage from "./components/Podcast/PodcastHubPage";
import PodcastEpisodePage from "./components/Podcast/PodcastEpisodePage";
import PodcastGuestPage from "./components/Podcast/PodcastGuestPage";
import ContactPage from "./components/Marketing/ContactPage";
import BookDemoPage from "./components/Marketing/BookDemoPage";
import StoryPage from "./components/Story/StoryPage";
import SecurityPage from "./components/Marketing/SecurityPage";
import BrandPage from "./components/Marketing/BrandPage";
import AdminHubPage from "./components/Admin/AdminHubPage";
import AdminScoreboardPage from "./components/Admin/AdminScoreboardPage";
import AdminPaymentsPage from "./components/Admin/AdminPaymentsPage";
import AdminMarketingPagesPage from "./components/Admin/AdminMarketingPagesPage";
import AdminHirerAccountsPage from "./components/Admin/AdminHirerAccountsPage";
import AdminLifecycleEmailsPage from "./components/Admin/AdminLifecycleEmailsPage";
import ForAgentsPage from "./components/Marketing/ForAgentsPage";
import MusePage from "./components/Marketing/MusePage";
import SuperAgentsPage from "./components/SuperAgents/SuperAgentsPage";
import AgentToAgentPage from "./components/AgentToAgent/AgentToAgentPage";
import IntegrationLandingPage from "./components/Marketing/IntegrationLandingPage";
import { INTEGRATION_CONFIGS } from "./components/Marketing/integrationConfigs";
import RootRoute from "./components/Marketing/RootRoute";
import ScrollToTop from "./components/Marketing/ScrollToTop";
import BootScreen from "./components/Common/BootScreen";
import { fromAppCore, fromAdminCore, GlobalLayout } from "./appChunks";
import { lazyWithReload } from "./utils/lazyRetry";
// Static on purpose: a 40-line theme wrapper whose only imports (antd ConfigProvider, ThemeContext)
// are already in this entry bundle; the never-static rule below is for PAGES.
import ProjectsTheme from "./components/Project/ProjectsTheme";
import { AgentComputerProvider } from "./contexts/AgentComputerContext";
import { HumanComputerProvider } from "./contexts/HumanComputerContext";
import { HireProposalProvider } from "./contexts/HireProposalContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import usePushAccountSync from "./hooks/usePushAccountSync";
import { BillingProvider } from "./contexts/BillingContext";
import { CellCogAccessProvider } from "./contexts/CellCogAccessContext";
import { ChatListProvider } from "./contexts/ChatListContext";
import { ChannelListProvider } from "./contexts/ChannelListContext";
import { PresenceProvider } from "./contexts/PresenceProvider";
import { EmployeeListProvider } from "./contexts/EmployeeListContext";
import { ApprovalStampProvider } from "./contexts/ApprovalStampContext";
import { SubagentRunningProvider } from "./contexts/SubagentRunningContext";
import { CookieConsentProvider, useCookieConsent } from "./contexts/CookieConsentContext";
import { CurrentChatProvider } from "./contexts/CurrentChatContext";
import { DomainProvider } from "./contexts/DomainContext";
import { MediaViewerProvider } from "./contexts/MediaViewerContext";
import { EmployeeDashboardProvider } from "./contexts/EmployeeDashboardContext";


import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { ApiKeyManagerProvider } from "./components/ApiKey";
import { DelegationStatsProvider } from "./components/Connectors/hooks/useDelegationStats";

import "./styles/shared-backgrounds.css";
import { initGA, initializeConsentMode, reinitializeGAAfterConsent, trackPageView } from './utils/analytics';
import { updateCookieConsent } from './services/userApi';
import { installAppClickTracking } from './utils/appClickTracking';
import { cleanupStaleDrafts } from './utils/editorStateStorage';
import NotificationSoundManager from './utils/NotificationSoundManager';

// ---------------------------------------------------------------------------
// CEL-1086 auth-first bundle split. Everything behind auth loads lazily from
// the app-core chunk (src/AppCore.js); the admin dashboards from admin-core
// (src/AdminCore.js). Marketing/auth/SEO pages stay STATIC in the entry —
// they're react-snap prerendered, and lazy-loading them would swap the
// already-painted static page to a blank fallback during boot. Definitions
// are module-level so each lazy component keeps a stable identity across
// renders. GlobalLayout's lazy instance lives in appChunks.js (shared with
// RootRoute / MarketingRoute). Adding an authed page: export it from
// AppCore.js and add a fromAppCore line here — NEVER a static import (that
// silently drags the page back into the entry bundle).
const ChatPage = fromAppCore("ChatPage");
const ChannelPage = fromAppCore("ChannelPage");
const NewEmployeePage = fromAppCore("NewEmployeePage");
// Org-wide AI employee visibility (agent_docs/employee_org_visibility_prd.md R4/R5): the public card + the
// request-access email's one-click share confirm. Both routes sit BEFORE /ai_employee/:employeeId.
const EmployeeProfilePage = fromAppCore("EmployeeProfilePage");
const EmployeeShareConfirmPage = fromAppCore("EmployeeShareConfirmPage");
const EmployeeTasksPage = fromAppCore("EmployeeTasksPage");
const RoutinesPage = fromAppCore("RoutinesPage");
const DrivePage = fromAppCore("DrivePage");
const ColdOutreachPage = fromAppCore("ColdOutreachPage");
const HumanComputerPage = fromAppCore("HumanComputerPage");
const BrowseMyChromePage = fromAppCore("BrowseMyChromePage");
const ProjectsPage = fromAppCore("ProjectsPage");
const ProjectDetailsPage = fromAppCore("ProjectDetailsPage");
const AvatarsPage = fromAppCore("AvatarsPage");
const AvatarDetailsPage = fromAppCore("AvatarDetailsPage");
const CloudBrowsersPage = fromAppCore("CloudBrowsersPage");
const LiveBrowserPage = fromAppCore("LiveBrowserPage");
const PhonesPage = fromAppCore("PhonesPage");
const ContactsPage = fromAppCore("ContactsPage");
const SupportDeskPage = fromAppCore("SupportDeskPage");
const SpendPage = fromAppCore("SpendPage");
const SocialPage = fromAppCore("SocialPage");
const UsagePage = fromAppCore("UsagePage");
const SubagentsPage = fromAppCore("SubagentsPage");
const AdsPage = fromAppCore("AdsPage");
// Connectors is ONE page since Sep 14 2026: /connectors, /connectors/tools, /connectors/platforms and
// /connectors/logins render ConnectorsHub with that card selected (deep links keep landing right);
// /connectors/delegations redirects to the Delegate app since Oct 2 2026 (CEL-3067).
const ConnectorsHub = fromAppCore("ConnectorsHub");
const KeysPage = fromAppCore("KeysPage");
const McpServersPage = fromAppCore("McpServersPage");
const GoogleDriveDetail = fromAppCore("GoogleDriveDetail");
const LinearDetail = fromAppCore("LinearDetail");
const OpenClawDetail = fromAppCore("OpenClawDetail");
// The Delegate app (Sep 21 2026): the sidebar home of MCP + Skills; /connectors/delegations/mcp redirects into it.
const DelegatePage = fromAppCore("DelegatePage");
const DelegateSkillDetail = fromAppCore("DelegateSkillDetail");
// Agent setup pages with no older detail page (Delegate v3, CEL-3067, Oct 2 2026).
const HermesDelegatePage = fromAppCore("HermesDelegatePage");
const MuseDelegatePage = fromAppCore("MuseDelegatePage");

/** An old path that moved: redirect and keep the query string and hash (OAuth returns ride the query). */
const KeepQueryRedirect = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};
// CellCog's OWN remote MCP server (CEL-2750): the OAuth consent card at /authorize?request=<id>.
const McpAuthorize = fromAppCore("McpAuthorize");
const DesktopConnect = fromAppCore("DesktopConnect");
const CursorDetail = fromAppCore("CursorDetail");
const ClaudeCodeDetail = fromAppCore("ClaudeCodeDetail");
const ToolDetail = fromAppCore("ToolDetail");
// Triggers v1 (contract §7): its own lazy chunk (never a static import); may move into AppCore.js as a
// fromAppCore line later (AppCore.js was outside this build's file fence).
const TriggerLinkPage = lazyWithReload(() => import("./components/Connectors/TriggerLinkPage"));
const OtherAgentsDetail = fromAppCore("OtherAgentsDetail");
const ReferralsPage = fromAppCore("ReferralsPage");
const OrganizationPage = fromAppCore("OrganizationPage");
const UserProfile = fromAppCore("UserProfile");
const WelcomePage = fromAppCore("WelcomePage");
const AdminUserDashboard = fromAdminCore("AdminUserDashboard");
const AdminChatDashboard = fromAdminCore("AdminChatDashboard");
const AdminReferralsPage = fromAdminCore("AdminReferralsPage");
const AdminLeadDashboard = fromAdminCore("AdminLeadDashboard");
const AdminAnnouncementsPage = fromAdminCore("AdminAnnouncementsPage");
const AdminOutreachLedger = fromAdminCore("AdminOutreachLedger");
const AdminBlockedDomainsPage = fromAdminCore("AdminBlockedDomainsPage");
const AdminPhoneRatesPage = fromAdminCore("AdminPhoneRatesPage");
const AdminBlogCallsPage = fromAdminCore("AdminBlogCallsPage");
const AdminLifecycleControlsPage = fromAdminCore("AdminLifecycleControlsPage");
// Admin V2 AI employees list (Oct 16 2026): its own lazy chunk because AdminCore.js was outside that build's file
// fence (the TriggerLinkPage precedent above); may move into AdminCore.js as a fromAdminCore line later.
const AdminEmployeesPage = lazyWithReload(() => import("./components/Admin/AdminEmployeesPage"));

// Component to track page views on route changes
const PageViewTracker = () => {
  const location = useLocation();
  // "/" is dual-use: marketing homepage (logged out) vs the app home state
  // (logged in). Pass auth state so analytics reports the logged-in view under
  // a virtual path (/app-home) and landing-page numbers stay clean. AppContent
  // mounts only after AuthProvider resolves, so currentUser is settled by first fire.
  const { currentUser } = useAuth();

  useEffect(() => {
    // Track page view on route change (only if GA4 is initialized)
    trackPageView(location.pathname, document.title, !!currentUser);
  }, [location.pathname, currentUser]);

  // App-click layer (Aug 21 2026): ONE idempotent delegated listener; elements
  // carrying data-track fire app_click with controlled properties. See
  // utils/appClickTracking.js + TRACKING.md ("App surfaces").
  useEffect(() => { installAppClickTracking(); }, []);

  return null;
};


// AccountConsentSync (geo-split v3, CEL-1678): consent follows the ACCOUNT.
// Down: on login, the profile's cookie_consent (if the user ever chose while
// logged in) OVERRIDES localStorage via applyExternalConsent — no re-asking on
// a fresh device. Up: any consent state change while logged in writes through
// to PUT /user/cookie-consent (fire-and-forget; 403 during admin spoof
// sessions is a deliberate server guard — silent no-op). An echo-guard ref
// prevents the down-sync from immediately PUT-ing the same value back up.
const AccountConsentSync = () => {
  const { currentUser, userProfile } = useAuth();
  const { consentGiven, analyticsConsent, applyExternalConsent } = useCookieConsent();
  const lastSynced = React.useRef(null); // last analytics value known to match the server

  // Down-sync: server value wins on login/profile load.
  useEffect(() => {
    const server = userProfile && userProfile.cookie_consent;
    if (!server || typeof server.analytics !== 'boolean') return;
    lastSynced.current = server.analytics;
    if (!consentGiven || analyticsConsent !== server.analytics) {
      applyExternalConsent(server.analytics);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  // Up-sync: a local choice (banner / settings / GPC) persists to the account.
  useEffect(() => {
    if (!currentUser || !consentGiven) return;
    if (lastSynced.current === analyticsConsent) return;
    lastSynced.current = analyticsConsent;
    updateCookieConsent(analyticsConsent, 'settings').catch(function () {
      /* spoof-guard 403 or transient network — localStorage still holds the choice */
    });
  }, [currentUser, consentGiven, analyticsConsent]);

  return null;
};

// Component to initialize GA4 based on consent
const GAInitializer = () => {
  const { geoDetected, isEU, analyticsConsent, consentGiven } = useCookieConsent();
  const [gaInitialized, setGaInitialized] = useState(false);

  useEffect(() => {
    // Wait for geo-detection to complete
    if (!geoDetected) return;

    // Geo-split v3 (Aug 24): isEU now means "strict opt-in bucket" (EU/UK/CH +
    // CN + CA + KR — geoDetection.STRICT_CONSENT_COUNTRIES). The quiet bucket
    // defaults analytics ON, but a stored rejection (Cookie preferences) or a
    // GPC signal arrives as consentGiven && !analyticsConsent and must stick —
    // the old unconditional initGA(false, true) ignored saved rejections.
    const granted = isEU
      ? (consentGiven && analyticsConsent)
      : (consentGiven ? analyticsConsent : true);

    // Initialize Consent Mode first
    if (!gaInitialized) {
      initializeConsentMode(isEU, granted);
    }

    // Initialize GA4 based on user type and consent
    if (!gaInitialized) {
      if (!isEU) {
        if (granted) {
          initGA(false, true);
          setGaInitialized(true);
        }
        // Rejected/GPC quiet-bucket visitors: consent mode stays denied and GA
        // unloaded; a later re-grant re-runs this effect and loads it then.
      } else if (consentGiven && analyticsConsent) {
        // EU users who consented: Initialize GA4
        initGA(true, true);
        setGaInitialized(true);
      } else if (consentGiven && !analyticsConsent) {
        // EU users who rejected: Don't initialize GA4
        console.log('[App] GA4 NOT initialized - EU user rejected cookies');
      }
      // If EU user hasn't made choice yet, wait for consent
    }
  }, [geoDetected, isEU, analyticsConsent, consentGiven, gaInitialized]);

  // Re-initialize GA4 when EU user grants consent
  useEffect(() => {
    if (geoDetected && isEU && analyticsConsent && !gaInitialized) {
      reinitializeGAAfterConsent();
      setGaInitialized(true);
      console.log('[App] GA4 re-initialized after consent granted');
    }
  }, [geoDetected, isEU, analyticsConsent, gaInitialized]);

  return null;
};

/**
 * AdaptiveAuthThemeBridge (CEL-1627) — feeds the current route into
 * ThemeContext so the system-adaptive AUTH pages (/login, /signup) can
 * resolve the visitor's real theme instead of the logged-out dark pin.
 * Lives here because ThemeProvider mounts ABOVE Router and cannot
 * useLocation itself; renders nothing. ThemeContext also self-initializes
 * from window.location, so direct loads are correct before this mounts —
 * this bridge covers client-side navigation (e.g. /pricing → /signup and
 * back, where the dark pin must return).
 */
const AdaptiveAuthThemeBridge = () => {
  const { setAdaptiveAuthPath } = useTheme();
  const { pathname } = useLocation();
  useEffect(() => {
    setAdaptiveAuthPath(isAdaptiveAuthPath(pathname));
  }, [pathname, setAdaptiveAuthPath]);
  return null;
};

const AppContent = () => {
  const { currentUser, userProfile, profileLoading, refreshUserProfile } = useAuth();
  const [hasCheckedUpdates, setHasCheckedUpdates] = useState(false);
  const { theme } = useTheme();
  // Browser notifications (CEL-2490): register a granted browser under the signed-in account; record notification taps.
  usePushAccountSync();

  // Cleanup stale drafts on app initialization
  useEffect(() => {
    cleanupStaleDrafts();
    NotificationSoundManager.init();
    // Tear down the anti-flash boot splash (public/index.html) now that the app
    // is rendering for real. AppContent mounts only AFTER AuthProvider resolves
    // (it gates {!loading && children}), so reaching here means auth is settled
    // and the correct surface (app vs marketing) is about to paint — the exact
    // moment to reveal #root. No-op when the splash was never shown.
    if (window.__ccRemoveSplash) window.__ccRemoveSplash();
  }, []);

  useEffect(() => {
    if (currentUser && !profileLoading && userProfile && !hasCheckedUpdates) {
      checkForUnseenUpdates();
    }
  }, [currentUser, profileLoading, userProfile, hasCheckedUpdates]);

  const checkForUnseenUpdates = async () => {
    try {
      const params = {
        post_type: 'major_update,minor_update',
        limit: 1,
        ...(userProfile.last_seen_post_id && { after_id: userProfile.last_seen_post_id })
      };

      const response = await getPosts(params);
      // Product updates modal is deprecated (pending redesign): silently mark
      // the newest unseen update as seen — same call the modal made on open —
      // so last_seen_post_id keeps advancing without disrupting the user.
      if (response.data.posts.length > 0) {
        await markUpdatesAsSeen(response.data.posts[0].id);
        if (refreshUserProfile) {
          await refreshUserProfile();
        }
      }
      setHasCheckedUpdates(true);
    } catch (error) {
      console.error('Failed to check for updates:', error);
      setHasCheckedUpdates(true);
    }
  };


  return (
    <ConfigProvider theme={theme}>
      {/* Initialize GA4 based on consent */}
      <GAInitializer />
      <AccountConsentSync />

      {/* Reset scroll to top on every route change */}
      <ScrollToTop />

      {/* Adaptive auth pages: route → ThemeContext (CEL-1627) */}
      <AdaptiveAuthThemeBridge />

      {/* Track page views on route changes */}
      <PageViewTracker />

      {/* Cookie Consent Banner (geo-targeted) */}
      <CookieBanner />

      <Routes>
        {/* Auth pages with GlobalLayout */}
        <Route path="/login" element={
          <MarketingRoute>
            <AuthPage mode="login" />
          </MarketingRoute>
        } />

        <Route path="/signup" element={
          <MarketingRoute>
            <AuthPage mode="signup" />
          </MarketingRoute>
        } />

        <Route path="/forgot-password" element={
          <MarketingRoute>
            <ForgotPassword />
          </MarketingRoute>
        } />

        <Route path="/auth/action" element={
          <MarketingRoute>
            <EmailActionHandler />
          </MarketingRoute>
        } />

        <Route path="/auth/electron-callback" element={
          <ElectronAuthCallback />
        } />

        {/* One-org model M2 (multi_org_v2_prd.md rev 5): the account-deactivation lock screen.
            Deliberately NOT inside ProtectedRoute — every API call from a locked account is
            rejected (the axios interceptor routes here), so this page must render standalone. */}
        <Route path="/account-deactivated" element={<AccountDeactivated />} />
        <Route path="/verify-email-required" element={<VerifyEmailRequired />} />

        <Route path="/posts" element={
          <MarketingRoute>
            <PostsPage />
          </MarketingRoute>
        } />

        <Route path="/posts/:postId" element={
          <MarketingRoute>
            <SinglePostPage />
          </MarketingRoute>
        } />

        {/* /new = ALIAS of "/" (New Chat is the signed-in front door again since 2026-09-16, Nitish; it was
            the real New Chat route 09-12 → 09-16 while "/" was the hire page). RootRoute redirects it to
            "/" with the query kept, so the links minted in that window still land on New Chat. */}
        <Route path="/new" element={<RootRoute surface="chat" />} />

        {/* One-time post-consent welcome page (starter credits + subscription
            cards). Stateless: nothing persists about seeing it — refresh/away
            and the user is in the app like anyone else. */}
        <Route path="/welcome" element={
          <ProtectedRoute>
            <WelcomePage />
          </ProtectedRoute>
        } />

        {/* CellCog's OWN remote MCP server (CEL-2750): the OAuth consent card. Signed-in, no
            GlobalLayout (a flow moment like /welcome). A signed-out visitor goes through /login;
            ProtectedRoute saves this URL, query included, on location.state and Login/AuthPage
            navigate back to it. Not a public route: no sitemap, react-snap or .md alternate; the LB
            allowlist needs `/authorize` (coordinator, infra/lb/tradewise-lb.urlmap.yaml). */}
        <Route path="/authorize" element={
          <ProtectedRoute>
            <McpAuthorize />
          </ProtectedRoute>
        } />

        {/* CellCog Desktop connects in the user's own browser (Desktop login v1, CEL-3612): Desktop opens
            /desktop/connect?code=XXXX-XXXX and polls the server while the signed-in person presses Connect. Signed-in,
            no GlobalLayout (a focused card like /authorize); a signed-out visitor goes through /login and ProtectedRoute
            brings them back with the code. Not a public route: no sitemap, react-snap or .md alternate; the LB allowlist
            carries /desktop/connect (infra/lb/tradewise-lb.urlmap.yaml). */}
        <Route path="/desktop/connect" element={
          <ProtectedRoute>
            <DesktopConnect />
          </ProtectedRoute>
        } />

        <Route path="/chat/:chatId" element={
          <ProtectedRoute>
            <GlobalLayout>
              <AgentComputerProvider>
                {/* CurrentChatProvider and HumanComputerProvider are both
                    mounted globally in the root provider tree. Nesting
                    them again here would create second context instances
                    and double-subscribe to the WebSocket / chat metadata
                    pipes — keep this route lean. */}
                <ChatPage />
              </AgentComputerProvider>
            </GlobalLayout>
          </ProtectedRoute>
        } />

        <Route path="/ai_employee" element={
          <ProtectedRoute>
            <GlobalLayout>
              <NewEmployeePage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        <Route path="/ai_employee/:employeeId/profile" element={
          <ProtectedRoute>
            <GlobalLayout>
              <EmployeeProfilePage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        <Route path="/ai_employee/:employeeId/share" element={
          <ProtectedRoute>
            <GlobalLayout>
              <EmployeeShareConfirmPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        <Route path="/ai_employee/:employeeId" element={
          <ProtectedRoute>
            <GlobalLayout>
              <AgentComputerProvider>
                {/* Unified renderer: ChatPage handles BOTH a normal chat (/chat/:chatId) and an AI
                    employee (/ai_employee/:employeeId). For an employee it resolves shifts and renders
                    the continuous shift stream via ChatWindow's preMessagesSlot; a single chat is the
                    degenerate 1-shift case. CurrentChatProvider/HumanComputerProvider are global. */}
                <ChatPage />
              </AgentComputerProvider>
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Agent Channels (CEL-748/750): org-public channels — agent-chat layout per design D8.1. */}
        <Route path="/channels/:channelId" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ChannelPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        <Route path="/tasks" element={
          <ProtectedRoute>
            <GlobalLayout>
              <EmployeeTasksPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Tasks v2 deep link: /tasks/CEL-123 opens the task modal on top of the board. */}
        <Route path="/tasks/:taskKey" element={
          <ProtectedRoute>
            <GlobalLayout>
              <EmployeeTasksPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Routines (CEL-1694): the org-wide routine board — the /tasks sibling. */}
        <Route path="/routines" element={
          <ProtectedRoute>
            <GlobalLayout>
              <RoutinesPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Projects routes */}
        {/* Projects carry the Projects tile colour (brown) as their primary — ProjectsTheme (Nitish, Sep 11 2026). */}
        <Route path="/projects" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ProjectsTheme>
                <ProjectsPage />
              </ProjectsTheme>
            </GlobalLayout>
          </ProtectedRoute>
        } />

        <Route path="/projects/:projectId" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ProjectsTheme>
                <ProjectDetailsPage />
              </ProjectsTheme>
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Drive route */}
        <Route path="/drive" element={
          <ProtectedRoute>
            <GlobalLayout>
              <DrivePage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Cowork route */}
        <Route path="/cowork" element={
          <ProtectedRoute>
            <GlobalLayout>
              <HumanComputerPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Browse my Chrome: peer of Cowork. Shares the
            Desktop-app precursor; owns its own setup story (extension
            install per Chrome profile + threat threshold). */}
        <Route path="/cowork/browse" element={
          <ProtectedRoute>
            <GlobalLayout>
              <BrowseMyChromePage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Cold Outreach route (AI-Employee management surface) */}
        <Route path="/outreach" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ColdOutreachPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Avatars routes */}
        <Route path="/avatars" element={
          <ProtectedRoute>
            <GlobalLayout>
              <AvatarsPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        <Route path="/avatars/:avatarId" element={
          <ProtectedRoute>
            <GlobalLayout>
              <AvatarDetailsPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Cloud Browsers — the agent browser's global surface (cloud_browsers_prd.md) */}
        <Route path="/cloud-browsers" element={
          <ProtectedRoute>
            <GlobalLayout>
              <CloudBrowsersPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* One browser, full-screen takeover (cloud_browsers_v2 ruling 0.3) */}
        <Route path="/cloud-browsers/:profileId" element={
          <ProtectedRoute>
            <GlobalLayout>
              <LiveBrowserPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Phones — the organization-level control plane for every AI employee's phone (agent_docs/employee_phone/
            PHONES_GLOBAL_APP_DESIGN.md). The page wraps itself in AppAccentTheme (pink) like Avatars and Drive. */}
        <Route path="/phones" element={
          <ProtectedRoute>
            <GlobalLayout>
              <PhonesPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Contacts — the organization's shared memory of everyone outside it that its AI employees email, call and meet
            (agent_docs/contacts/CONTACTS_CONTRACT.md §8). The page wraps itself in AppAccentTheme (violet) like Phones. */}
        <Route path="/contacts" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ContactsPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Support (Oct 26 2026; agent_docs/support_widget/PHASE3_CONTRACT.md section 5): the Support Desk, where an organization's
            AI employees answer customers from the site widget and a person can join. `/support` is the help center. Behind
            SUPPORT_ROLLOUT (the page reads a 404 as "not available"). The page wraps itself in AppAccentTheme (deep blue). In-app
            (not prerendered). The LB URL map must allowlist /support-desk and /support-desk/* (Arjun's sync). */}
        <Route path="/support-desk" element={
          <ProtectedRoute>
            <GlobalLayout>
              <SupportDeskPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Spend: the owner's Link wallet, which AI employees may buy things and every purchase (Payments v1, Nitish
            Oct 3 2026). Link's OAuth callback returns here with ?link=connected or ?link_error=<code>. */}
        <Route path="/spend" element={
          <ProtectedRoute>
            <GlobalLayout>
              <SpendPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Social (Sep 25 2026, Nitish; agent_docs/social_app/SOCIAL_APP_PLAN.md v3): every connected social account in one
            snapshot. The page wraps itself in AppAccentTheme (sky). The LB URL map allowlists /social. */}
        <Route path="/social" element={
          <ProtectedRoute>
            <GlobalLayout>
              <SocialPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Usage (Oct 9 2026, Nitish; agent_docs/usage/USAGE_CONTRACT.md): where the organization's credits go, read from the
            credit ledger. ?range=7|30|90|period, ?chat=<id> (the chat header's View usage). The page wraps itself in
            AppAccentTheme (deep sky). The LB URL map allowlists /usage. */}
        <Route path="/usage" element={
          <ProtectedRoute>
            <GlobalLayout>
              <UsagePage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Subagents (Oct 10 2026, Nitish; agent_docs/subagents_v1_prd.md section 10): every subagent the account's agents
            launched in the current organization, grouped under its parent, plus readiness per kind. In-app (not prerendered).
            The page wraps itself in AppAccentTheme (deep jade). The LB URL map allowlists /subagents. */}
        <Route path="/subagents" element={
          <ProtectedRoute>
            <GlobalLayout>
              <SubagentsPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Ads (Sep 25 2026, Nitish; agent_docs/ads_app/ADS_APP_PLAN.md v1): every connected ad account in one snapshot. The page
            wraps itself in AppAccentTheme (deep gold). The LB URL map allowlists /ads. */}
        <Route path="/ads" element={
          <ProtectedRoute>
            <GlobalLayout>
              <AdsPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Delegate (Sep 21 2026): your agents into CellCog. /delegate = the MCP address + one card per client +
            connected apps; /delegate/skills = the skills catalog inside the app. The LB URL map allowlists both. */}
        <Route path="/delegate" element={
          <ProtectedRoute>
            <GlobalLayout>
              <DelegatePage />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        <Route path="/delegate/skills" element={
          <ProtectedRoute>
            <GlobalLayout>
              <DelegatePage />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        {/* A skill opened from Delegate > Skills stays inside the app (Sep 23 2026, Nitish); the public
            /skills/:skillSlug page below stays the marketing home. Covered by the LB's /delegate/* rule. */}
        <Route path="/delegate/skills/:skillSlug" element={
          <ProtectedRoute>
            <GlobalLayout>
              <DelegateSkillDetail />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Delegate v3 (CEL-3067, Oct 2 2026): every agent page lives under /delegate. The old
            /connectors/delegations/* paths redirect here keeping the query string (Linear's OAuth return). */}
        <Route path="/delegate/openclaw" element={
          <ProtectedRoute>
            <GlobalLayout>
              <OpenClawDetail />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        <Route path="/delegate/cursor" element={
          <ProtectedRoute>
            <GlobalLayout>
              <CursorDetail />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        <Route path="/delegate/claude-code" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ClaudeCodeDetail />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        <Route path="/delegate/other" element={
          <ProtectedRoute>
            <GlobalLayout>
              <OtherAgentsDetail />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        <Route path="/delegate/linear" element={
          <ProtectedRoute>
            <GlobalLayout>
              <LinearDetail />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        <Route path="/delegate/hermes" element={
          <ProtectedRoute>
            <GlobalLayout>
              <HermesDelegatePage />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        <Route path="/delegate/muse" element={
          <ProtectedRoute>
            <GlobalLayout>
              <MuseDelegatePage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Skills routes (public - no auth required) */}
        <Route path="/skills" element={
          <MarketingRoute>
            <SkillsPage />
          </MarketingRoute>
        } />
        <Route path="/skills/:skillSlug" element={
          <MarketingRoute>
            <SkillDetailPage />
          </MarketingRoute>
        } />

        {/* Support docs route (public - no auth required) */}
        <Route path="/support" element={
          <MarketingRoute>
            <SupportDocsPage />
          </MarketingRoute>
        } />
        <Route path="/support/:docSlug" element={
          <MarketingRoute>
            <SupportDocsPage />
          </MarketingRoute>
        } />

        {/* Pricing page (public). Logged-out → marketing shell; logged-in → app shell. */}
        <Route path="/pricing" element={
          <MarketingRoute>
            <PricingPage />
          </MarketingRoute>
        } />

        {/* Tutorials (CEL-1157, public). Hub + per-video pages; the dynamic
            route validates the slug against LIVE config entries and soft-
            redirects unknown/non-live slugs to the hub (roleConfigs pattern).
            React Router ranks the exact /tutorials route above the param
            route, so the hub is never shadowed. */}
        <Route path="/tutorials" element={
          <MarketingRoute>
            <TutorialsHubPage />
          </MarketingRoute>
        } />
        <Route path="/tutorials/:tutorialSlug" element={
          <MarketingRoute>
            <TutorialVideoPage />
          </MarketingRoute>
        } />

        {/* Podcast (CEL-1355, public). Hub + per-episode pages; the dynamic
            route validates the slug against LIVE episode bundles and soft-
            redirects unknown/non-live slugs to the hub (tutorials pattern).
            Exact /podcast route ranks above the param route. */}
        <Route path="/podcast" element={
          <MarketingRoute>
            <PodcastHubPage />
          </MarketingRoute>
        } />
        {/* Be a guest (CEL-2495): the page a human forwards to their agent. A static
            segment above the episode param route (server-side, podcast_pages.py
            registers its route before the {slug} route for the same reason) so
            "guests" is never read as an episode slug. Content = guestGuide.json: one
            source for this page, the SSR crawler HTML and /podcast/guests/index.md. */}
        <Route path="/podcast/guests" element={
          <MarketingRoute>
            <PodcastGuestPage />
          </MarketingRoute>
        } />
        <Route path="/podcast/:episodeSlug" element={
          <MarketingRoute>
            <PodcastEpisodePage />
          </MarketingRoute>
        } />

        {/* Linear integration landing page (public - marketing + first-connect) */}
        {/* Marketing brand pages (public). Logged-out → dark MarketingLayout (top
            header, no sidebar). Logged-in → GlobalLayout (app shell, unchanged). */}
        <Route path="/ai-employees" element={
          <MarketingRoute>
            <AIEmployeesLandingPage />
          </MarketingRoute>
        } />
        {/* Back-compat: old underscored slug → hyphenated canonical */}
        <Route path="/ai_employees" element={<Navigate to="/ai-employees" replace />} />

        {/* Programmatic-SEO role pages (/ai-employees/ai-{role-slug}).
            ONE dynamic route; RoleLandingPage validates :roleSlug against
            roleConfigs.js (unknown slug → redirect /ai-employees). Adding a
            new role = a config entry + reactSnap.include + seo.py, no route
            change. React Router v6 ranks the exact /ai-employees route above
            this param route, so the hub page is unaffected. */}
        <Route path="/ai-employees/:roleSlug" element={
          <MarketingRoute>
            <RoleLandingPage />
          </MarketingRoute>
        } />

        {/* GTM beachhead vertical pages (/ai-employees/for-{industry}, Aug 2026:
            home services + real-estate teams). Nested under /ai-employees so the
            existing LB allow-list covers them; static routes outrank the
            /ai-employees/:roleSlug dynamic route, so no collision. Adding
            vertical #3: config entry + a route here + reactSnap.include +
            seo.py + footer link + OG image. */}
        {/* AI employees in meetings (/ai-employees/meetings, Nitish-ruled Sep 29 2026): the link
            destination for the meeting films, clips and follow-ups. Static route, so it outranks
            /ai-employees/:roleSlug; the LB allow-list (/ai-employees/*) already covers it. */}
        <Route path="/ai-employees/meetings" element={
          <MarketingRoute>
            <MeetingsLandingPage />
          </MarketingRoute>
        } />
        <Route path="/ai-employees/for-home-services" element={
          <MarketingRoute>
            <VerticalLandingPage verticalSlug="for-home-services" />
          </MarketingRoute>
        } />
        <Route path="/ai-employees/for-real-estate-teams" element={
          <MarketingRoute>
            <VerticalLandingPage verticalSlug="for-real-estate-teams" />
          </MarketingRoute>
        } />
        <Route path="/ai-employees/for-shopify-stores" element={
          <MarketingRoute>
            <VerticalLandingPage verticalSlug="for-shopify-stores" />
          </MarketingRoute>
        } />

        {/* Compare hub (/compare) — the index of the whole comparison family and the
            canonical internal-linking surface for /compare/{slug} pages (the footer
            keeps a trimmed selection). Roster derives from compareConfigs.js. */}
        <Route path="/compare" element={
          <MarketingRoute>
            <CompareHubPage />
          </MarketingRoute>
        } />

        {/* Programmatic-SEO comparison pages (/compare/{slug}) — same config-driven
            pattern as the role pages: ComparisonPage validates :compareSlug against
            compareConfigs.js (unknown slug → redirect to the category page). Adding
            a page = a config entry + reactSnap.include + seo.py, no route change. */}
        <Route path="/compare/:compareSlug" element={
          <MarketingRoute>
            <ComparisonPage />
          </MarketingRoute>
        } />

        {/* Benchmark receipts page (/benchmarks) — citable primary data for the
            DeepResearch Bench story; standings verified against the public
            leaderboard data (provenance + update procedure in BenchmarksPage.js). */}
        <Route path="/benchmarks" element={
          <MarketingRoute>
            <BenchmarksPage />
          </MarketingRoute>
        } />

        {/* Product Hunt campaign landing (/producthunt) — launch-day welcome
            path (Jul 29, 2026). NOINDEX by design: not in sitemap/robots Allow;
            PH listing links point here with UTMs (see ProductHuntPage.js). */}
        <Route path="/producthunt" element={
          <MarketingRoute>
            <ProductHuntPage />
          </MarketingRoute>
        } />

        {/* AI organization page (/ai-organization) — the self-organization
            flagship: our real org chart as citable primary content (provenance
            + canon rules in AIOrganizationPage.js). */}
        <Route path="/ai-organization" element={
          <MarketingRoute>
            <AIOrganizationPage />
          </MarketingRoute>
        } />

        {/* /proof retired 2026-08-20 (founder-presence de-emphasis, CEL-1488):
            content was redundant with /ai-organization + buying-page proof.
            Redirect kept because outreach emails linked cellcog.ai/proof. */}
        <Route path="/proof" element={<Navigate to="/ai-employees" replace />} />

        <Route path="/super-agents" element={
          <MarketingRoute>
            <SuperAgentsPage />
          </MarketingRoute>
        } />

        <Route path="/agent-to-agent" element={
          <MarketingRoute>
            <AgentToAgentPage />
          </MarketingRoute>
        } />

        <Route path="/contact" element={
          <MarketingRoute>
            <ContactPage />
          </MarketingRoute>
        } />

        {/* Book a demo (CEL-3640, Nitish Oct 22 2026): the Home + /ai-employees header
            pill lands here; Nitish's demo calendar embedded. Registrations: this route,
            adaptivePaths.js, seo.py sitemap, react-snap include, public/book-a-demo/index.md,
            public/llms.txt; LB URL map (Arjun, CEL-3655). */}
        <Route path="/book-a-demo" element={
          <MarketingRoute>
            <BookDemoPage />
          </MarketingRoute>
        } />

        {/* The CellCog Story — founder letter (public marketing page). */}
        <Route path="/story" element={
          <MarketingRoute>
            <StoryPage />
          </MarketingRoute>
        } />

        {/* Security (Sep 16 2026, Nitish): Google verification + CASA facts, data
            handling. The page the home / AIE trust tiles link to. Other
            registrations: adaptivePaths.js, footer (this build); react-snap,
            seo.py sitemap, .md alternate, llms.txt, security.txt (Rhea); LB URL map (Arjun). */}
        <Route path="/security" element={
          <MarketingRoute>
            <SecurityPage />
          </MarketingRoute>
        } />

        {/* Brand guidelines (CEL-3808, Oct 29 2026): logo, colours, type + logo pack and PDF
            downloads in public/brand/. Other registrations: adaptivePaths.js, react-snap,
            seo.py sitemap, public/brand/index.md, llms.txt. */}
        <Route path="/brand" element={
          <MarketingRoute>
            <BrandPage />
          </MarketingRoute>
        } />

        {/* For AI agents (/for-agents) — agent-readable web program (CEL-2097). The page an
            agent lands on when evaluating CellCog for its human; markdown alternate at
            public/for-agents/index.md. Registrations: this route + seo.py sitemap +
            react-snap include + LB URL map (Arjun, 2026-09-03). */}
        <Route path="/for-agents" element={
          <MarketingRoute>
            <ForAgentsPage />
          </MarketingRoute>
        } />

        {/* CellCog for Muse (/muse): the studio a Meta Muse sends work to (Nitish,
            2026-09-19). Markdown alternate at public/muse/index.md. Registrations:
            this route + seo.py sitemap + react-snap include + LB URL map (Arjun,
            CEL-2836). Hero video /assets/muse/hero.mp4 drops via the same task. */}
        <Route path="/muse" element={
          <MarketingRoute>
            <MusePage />
          </MarketingRoute>
        } />

        {/* Integration marketing pages (public). Logged-out → dark marketing
            shell (top header, no sidebar); logged-in → app shell. /linear and
            /openclaw keep their bespoke landing pages; hermes/cursor/claude-code/
            codex use the shared IntegrationLandingPage template. */}
        <Route path="/linear" element={
          <MarketingRoute>
            <LinearLandingPage />
          </MarketingRoute>
        } />

        {/* OpenClaw integration landing page (public - marketing + first-connect).
            CTA opens the ApiKeyManager modal. Intent=get_api_key is preserved
            through the signup flow for logged-out visitors. */}
        <Route path="/openclaw" element={
          <MarketingRoute>
            <OpenClawLandingPage />
          </MarketingRoute>
        } />

        <Route path="/hermes" element={
          <MarketingRoute>
            <IntegrationLandingPage config={INTEGRATION_CONFIGS.hermes} />
          </MarketingRoute>
        } />
        <Route path="/cursor" element={
          <MarketingRoute>
            <IntegrationLandingPage config={INTEGRATION_CONFIGS.cursor} />
          </MarketingRoute>
        } />
        <Route path="/claude-code" element={
          <MarketingRoute>
            <IntegrationLandingPage config={INTEGRATION_CONFIGS['claude-code']} />
          </MarketingRoute>
        } />
        <Route path="/codex" element={
          <MarketingRoute>
            <IntegrationLandingPage config={INTEGRATION_CONFIGS.codex} />
          </MarketingRoute>
        } />

        {/* Connectors — sidebar hub + class landings + detail pages (Phase 1) */}
        <Route path="/connectors" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ConnectorsHub />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        <Route path="/connectors/platforms" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ConnectorsHub />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        <Route path="/connectors/platforms/google-drive" element={
          <ProtectedRoute>
            <GlobalLayout>
              <GoogleDriveDetail />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        <Route path="/connectors/delegations" element={<KeepQueryRedirect to="/delegate" />} />
        {/* The agent pages moved to /delegate/<agent> (CEL-3067, Oct 2 2026); the old paths redirect with the
            query string so bookmarks, guides and an in-flight Linear OAuth return keep landing right. */}
        <Route path="/connectors/delegations/linear" element={<KeepQueryRedirect to="/delegate/linear" />} />
        <Route path="/connectors/delegations/openclaw" element={<KeepQueryRedirect to="/delegate/openclaw" />} />
        {/* /connectors/delegations/mcp moved into the Delegate app (Sep 21 2026): ONE MCP surface. The redirect
            keeps the guide's, the fork's and llms.txt's links alive (old routes survive as redirects, canon
            app-front-door). */}
        <Route path="/connectors/delegations/mcp" element={<Navigate to="/delegate" replace />} />
        <Route path="/connectors/delegations/cursor" element={<KeepQueryRedirect to="/delegate/cursor" />} />
        <Route path="/connectors/delegations/claude-code" element={<KeepQueryRedirect to="/delegate/claude-code" />} />
        <Route path="/connectors/delegations/other" element={<KeepQueryRedirect to="/delegate/other" />} />
        <Route path="/connectors/tools" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ConnectorsHub />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        {/* /connectors/keys — freeform API keys (keys_doctrine_prd.md). THE page
            agents point users to for raw credentials; keys stay CellCog-side. */}
        <Route path="/connectors/keys" element={
          <ProtectedRoute>
            <GlobalLayout>
              <KeysPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        {/* /connectors/mcps — user-registered remote MCP servers
            (mcp_connectors_v1_design.md §6). Agents point users here to plug in
            their own servers; tools ride the personal-tools rail. */}
        <Route path="/connectors/mcps" element={
          <ProtectedRoute>
            <GlobalLayout>
              <McpServersPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        {/* /connectors/logins: saved sign-ins for AI employees' cloud browsers (CEL-2934,
            browser_logins_vault_design.md §6). The hub with its fourth card (Logins) selected;
            ?site= and ?employee= prefill the add drawer (the agent's access-ask link). */}
        <Route path="/connectors/logins" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ConnectorsHub />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        {/* /connectors/payments moved to /spend (Oct 3 2026); the alias keeps any early link working. */}
        <Route path="/connectors/payments" element={<Navigate to="/spend" replace />} />
        {/* /connectors/tools/:slug — per-toolkit detail page. Replaces the old
            AgentToolCard inline connect + ToolsModal action list flow with a
            dedicated page matching LinearDetail / GoogleDriveDetail. */}
        <Route path="/connectors/tools/:slug" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ToolDetail />
            </GlobalLayout>
          </ProtectedRoute>
        } />
        {/* /connectors/triggers/:toolkit/:trigger: the enable-a-trigger link as a full page (Triggers v1,
            agent_docs/triggers/TRIGGERS_V1_CONTRACT.md §5/§7) for surfaces that cannot render the in-chat
            card. Every query value is a prefill; opening creates nothing. In-app route: the LB allowlist
            needs /connectors/triggers (coordinator). */}
        <Route path="/connectors/triggers/:toolkit/:trigger" element={
          <ProtectedRoute>
            <GlobalLayout>
              <TriggerLinkPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Referrals routes */}
        <Route path="/referrals" element={
          <ProtectedRoute>
            <GlobalLayout>
              <ReferralsPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        <Route path="/invitation/:referralCode" element={
          <MarketingRoute>
            <InvitationLandingPage />
          </MarketingRoute>
        } />

        {/* Organization route */}
        <Route path="/organization" element={
          <ProtectedRoute>
            <GlobalLayout>
              <OrganizationPage />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Redirect /billing to profile billing tab */}
        <Route path="/billing" element={
          <Navigate to="/profile?tab=billing" replace />
        } />

        {/* Profile route */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <GlobalLayout>
              <UserProfile />
            </GlobalLayout>
          </ProtectedRoute>
        } />

        {/* Admin routes. /admin = the hub (Nitish, Sep 16 2026): Users, Chats and AI employees
            (Admin V2, Oct 16 2026) in the menu, every other admin page a card here;
            /admin/scoreboard + /admin/hirers read the hourly metrics cache inside the app. */}
        <Route path="/admin" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminHubPage />
            </GlobalLayout>
          </AdminRoute>
        } />
        <Route path="/admin/scoreboard" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminScoreboardPage />
            </GlobalLayout>
          </AdminRoute>
        } />
        <Route path="/admin/payments" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminPaymentsPage />
            </GlobalLayout>
          </AdminRoute>
        } />
        <Route path="/admin/marketing-pages" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminMarketingPagesPage />
            </GlobalLayout>
          </AdminRoute>
        } />
        <Route path="/admin/hirers" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminHirerAccountsPage />
            </GlobalLayout>
          </AdminRoute>
        } />
        <Route path="/admin/users" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminUserDashboard />
            </GlobalLayout>
          </AdminRoute>
        } />

        <Route path="/admin/chats" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminChatDashboard />
            </GlobalLayout>
          </AdminRoute>
        } />

        <Route path="/admin/employees" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminEmployeesPage />
            </GlobalLayout>
          </AdminRoute>
        } />

        <Route path="/admin/referrals" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminReferralsPage />
            </GlobalLayout>
          </AdminRoute>
        } />

        <Route path="/admin/leads" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminLeadDashboard />
            </GlobalLayout>
          </AdminRoute>
        } />

        <Route path="/admin/announcements" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminAnnouncementsPage />
            </GlobalLayout>
          </AdminRoute>
        } />

        <Route path="/admin/outreach" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminOutreachLedger />
            </GlobalLayout>
          </AdminRoute>
        } />

        <Route path="/admin/blocked-domains" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminBlockedDomainsPage />
            </GlobalLayout>
          </AdminRoute>
        } />
        <Route path="/admin/phone-rates" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminPhoneRatesPage />
            </GlobalLayout>
          </AdminRoute>
        } />
        <Route path="/admin/blog-calls" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminBlogCallsPage />
            </GlobalLayout>
          </AdminRoute>
        } />

        <Route path="/admin/lifecycle" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminLifecycleControlsPage />
            </GlobalLayout>
          </AdminRoute>
        } />

        {/* Aria's Lifecycle Emails board, embedded (Nitish, Sep 16 2026): the read next to the controls. */}
        <Route path="/admin/lifecycle-emails" element={
          <AdminRoute>
            <GlobalLayout>
              <AdminLifecycleEmailsPage />
            </GlobalLayout>
          </AdminRoute>
        } />

        <Route path="/policies/privacy-policy" element={<Navigate to="/support/privacy-policy" replace />} />
        {/* Sep 7 2026: /policies/privacy fell through to "/" (only the -policy form was mapped). */}
        <Route path="/policies/privacy" element={<Navigate to="/support/privacy-policy" replace />} />
        <Route path="/policies/terms" element={<Navigate to="/support/terms-of-service" replace />} />

        <Route path="/showcase" element={<Navigate to="/posts" replace />} />
        <Route path="/" element={<RootRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ConfigProvider>
  );
};

const App = () => (
  <HelmetProvider>
    <CookieConsentProvider>
      <AuthProvider>
        <ThemeProvider>
          <DomainProvider>
            <CellCogAccessProvider>
              <BillingProvider>
                <ChatListProvider>
                <EmployeeListProvider>
                <ApprovalStampProvider>
                <SubagentRunningProvider>
                <ChannelListProvider>
                <PresenceProvider>
                    <MediaViewerProvider>
                      <Router>
                        <ApiKeyManagerProvider>
                          <DelegationStatsProvider>
                            {/* CurrentChatProvider lives ABOVE
                                HumanComputerProvider. This is structurally
                                required: HumanComputerProvider calls
                                useCurrentChat() and gates every chat-scoped
                                effect (pending-commands fetch, review-panel
                                open, sound notify) on `currentChat?.id`
                                being defined. When CurrentChatProvider was
                                only mounted inside `/chat/:chatId`, the
                                global HC provider saw a null context →
                                fetchPendingCommands never fired on chat
                                load → review panel was permanently empty
                                even though the backend had a pending batch.
                                CurrentChatProvider is safe to mount
                                globally: its effects gate on chatId being
                                set, so it's idle on non-chat routes. */}
                            <CurrentChatProvider>
                              <HumanComputerProvider>
                              {/* HireProposalProvider (agent_docs/employee_initiated_hire_prd.md): the
                                  chat's pending hire proposal slot — same shape as the HC pending batch,
                                  same slot rules, so it sits right beside HumanComputerProvider. */}
                              <HireProposalProvider>
                                {/* EmployeeDashboardProvider lives here (global, above the right panel)
                                    so the dashboard's data + single-pane expansion state survive the
                                    media viewer temporarily taking over the right panel. Idle until an
                                    employee dashboard sets its active employee. */}
                                <EmployeeDashboardProvider>
                                  {/* CEL-1086: the ONE Suspense boundary for the lazy
                                      app-core / admin-core chunks. BootScreen visually
                                      matches the index.html anti-flash splash, so a
                                      logged-in cold load reads as one continuous loading
                                      state. Static marketing/auth pages never suspend. */}
                                  <Suspense fallback={<BootScreen />}>
                                    <AppContent />
                                  </Suspense>
                                </EmployeeDashboardProvider>
                              </HireProposalProvider>
                              </HumanComputerProvider>
                            </CurrentChatProvider>
                          </DelegationStatsProvider>
                        </ApiKeyManagerProvider>
                      </Router>
                    </MediaViewerProvider>
                </PresenceProvider>
                </ChannelListProvider>
                </SubagentRunningProvider>
                </ApprovalStampProvider>
                </EmployeeListProvider>
                </ChatListProvider>
              </BillingProvider>
            </CellCogAccessProvider>
          </DomainProvider>
        </ThemeProvider>
      </AuthProvider>
    </CookieConsentProvider>
  </HelmetProvider>
);

export default App;
